import React, { useState } from 'react'
import PublicLayout, { JsonLd } from '../components/PublicLayout'
import SignupCTA from '../components/SignupCTA'
import {
  GRANTMAKERS, PUBLIC_CHARITY_GRANTMAKERS, GRANTS_PAID_PROSE, fmt,
} from '../corpus'

const FAQS = [
  {
    q: 'Where do the leads come from?',
    a: `From public IRS filings, the legal record of which organizations each grantmaker actually funds. That is ${fmt(GRANTMAKERS)} grantmakers and the ${GRANTS_PAID_PROSE} grants they have paid, and it covers both private foundations and the ${fmt(PUBLIC_CHARITY_GRANTMAKERS)} public charities that make grants of their own. We rank the ones giving to work like yours and surface how they give. We also watch federal grants from Grants.gov, refreshed regularly, with a link back to every original listing. Foundations are included in every paid plan, and higher plans simply show more leads per digest.`,
  },
  {
    // The old answer claimed "the best value of any comparable tool" and took a
    // swipe at seat licenses. Both removed: unprovable, and the site no longer
    // measures itself against products it will not name.
    q: 'How is this different from other grant-research tools?',
    a: 'Most tools are databases you log into and search yourself. Praenum comes to you: write your mission once, and it emails a ranked shortlist of the funders and grants worth your time. Two things follow from how it is built. It reads your organization’s own filings, so it knows which foundations have already funded you. And it matches on what your mission describes rather than on keywords you have to guess at. Foundation leads are in every paid plan, and there is nothing to log into unless you want to.',
  },
  {
    q: 'How are matches chosen?',
    a: 'Each opportunity is scored against your profile and only the strongest fits make your digest. The matching is deliberately conservative: we would rather show you fewer, highly relevant grants than bury a good one under junk. You can thumbs-up or thumbs-down any match to sharpen future digests.',
  },
  {
    q: 'When do digests arrive?',
    a: 'Every Tuesday morning. Pro and Consultant plans can opt into a daily digest. New opportunities are picked up within hours of being posted, so nothing waits a full week to reach you.',
  },
  {
    q: 'Will the emails land in spam?',
    a: 'Deliverability is a first-class concern, and a digest in spam is worthless. We send through a dedicated transactional email provider with proper authentication, not a generic Gmail or Outlook account, so digests reliably reach your inbox.',
  },
  {
    q: 'Do I need to log in to get value?',
    a: 'No. After a five-minute setup the digest arrives by email, and you never have to open anything. When you do want to dig, there is a lot waiting: search across your matched foundations or the whole corpus, a full profile for any funder with their filed giving history, the foundations that give alongside the ones already backing you, plus your focus areas and feedback controls. The email is the floor, not the ceiling.',
  },
  {
    q: 'Can I search the whole database myself?',
    a: `Yes. Every plan lets you search the grantmakers matched to your organization. Pro and Consultant add the rest of the corpus, all ${fmt(GRANTMAKERS)} of them, ranked against your mission. Each result will also tell you why it is, or is not, in your weekly digest, which is usually the more useful answer.`,
  },
  {
    q: 'Do you use my organization’s own 990?',
    a: 'Yes, when we can identify you. Your filings are public, and reading them is how Praenum knows which foundations have already funded you and which ones fund organizations like yours. If we cannot match your organization to a filing, what you get back is that we could not check, never that nobody funds you. Only about 13% of foundations itemize who they gave to, so a blank there is a gap in the IRS data rather than a fact about your organization.',
  },
  {
    q: 'How do you use AI?',
    // Revised 2026-09-24 with the privacy policy: the old answer ("two
    // things") predated the Anthropic reranker (ADR-0045) and said nothing
    // about a third party, and funder blurbs have been off since 2026-08-30.
    a: 'For three narrow jobs. First, it reads your mission and finds funders whose filings describe similar work, so you do not have to guess at keywords. Second, it ranks the foundations it found: Anthropic’s Claude, an AI model from an outside company, reads each foundation’s filed grants next to your mission, location, focus areas and grant range, and the strongest are sent first. Third, it writes the one-line explanation under each federal grant, which is checked against the source data before you see it, and anything it cannot support is removed rather than softened. The first and third run on our own servers. It does not write your application and it does not invent facts about a funder. Our privacy policy says exactly what Claude sees and what Anthropic may do with it.',
  },
  {
    q: 'Is Praenum a grant-writing service?',
    a: 'No. Praenum surfaces the opportunities you qualify for; it does not write applications. Think of it as the early-warning system that makes sure your team never misses a deadline worth pursuing.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Manage, switch, or cancel your plan in one click from your account. Billing is handled securely by Stripe, with no contracts and no phone calls.',
  },
  {
    // Softened from a boundary to a design intent: the segment is the
    // beachhead, not the limit (owner direction, 2026-08-11).
    q: 'What size nonprofit is this for?',
    a: 'It is built for small US nonprofits, typically $100K to $2M in annual budget, and for freelance grant writers monitoring several clients at once. That is who it was designed around rather than a limit on who it serves. The data covers grantmakers of every size, so a larger organization gets the same corpus and the same matching.',
  },
  {
    q: 'Where does the name Praenum come from?',
    a: 'It is shortened from praenumerando, built on the Latin prae, meaning "before" or "in advance." The related word praenumeration described one of the earliest subscription models: eighteenth-century publishers selling books that had not been printed yet, so the work could be paid for ahead of time. Both halves turned out to fit. Praenum is a subscription, and the entire point is that you hear about a grant while there is still time to write the application.',
  },
]

/**
 * schema.org FAQPage, derived from FAQS rather than written out again.
 *
 * Duplicating the answers here would create two copies of the same text that
 * drift apart, which is both a maintenance trap and the specific thing that
 * makes search engines discard structured data: markup that does not match what
 * the page visibly says.
 */
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-medium text-gray-900">{q}</span>
        <svg
          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20" fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-5 -mt-1 text-sm text-gray-600 leading-relaxed">{a}</div>
      )}
    </div>
  )
}

export default function FAQ() {
  return (
    <PublicLayout
      title="FAQ"
      description="How Praenum finds grants for your nonprofit: where the foundation leads come from, how matches are chosen, what the weekly digest contains, how your own 990 is used, and how billing works."
    >
      {/* Built from the same FAQS array the page renders, so the structured data
          cannot drift from the visible answers. Search engines treat a mismatch
          between the two as a reason to ignore the markup. */}
      <JsonLd data={FAQ_SCHEMA} />
      <section className="bg-gradient-to-b from-brand-100 to-white">
        <div className="max-w-3xl mx-auto px-4 pt-20 pb-10 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-brand-900">
            Frequently asked questions
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Everything about how Praenum finds your grants, delivers them, and bills you.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 pb-16">
        <div className="space-y-3">
          {FAQS.map(item => (
            <FaqItem key={item.q} q={item.q} a={item.a} />
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-gray-50 border border-gray-200 p-8 text-center">
          <h2 className="text-xl font-bold text-brand-900">Still have a question?</h2>
          <p className="mt-2 text-sm text-gray-600">
            Email us at{' '}
            <a href="mailto:hello@praenum.com" className="text-brand-500 hover:underline">
              hello@praenum.com
            </a>{' '}
            and we'll get back to you.
          </p>
          <div className="mt-6 flex flex-col items-center">
            <SignupCTA size="lg" />
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
